import React, { useRef } from 'react';
import { Typography, BigChevronDownIcon } from '@forgeglobal/design-framework';
import usePanel from '@tcs/common/hooks/usePanel';
import { useStyles } from './style';
import { Variant } from '@mui/material/styles/createTypography';

export type SelectorValue = string | number;

export type SelectorItem = {
  value: SelectorValue;
  listItem: JSX.Element;
};

export interface SelectorProps {
  items: SelectorItem[];
  labelVariant: Variant;
  selectedValue: SelectorValue;
  onSelect: (value: SelectorValue) => void;
}

const Selector = ({
  items,
  labelVariant,
  onSelect,
  selectedValue,
}: SelectorProps) => {
  const classes = useStyles();
  const node = useRef(null);
  const [open, setOpen] = usePanel(node);
  const handleSelect = (value: SelectorValue) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value !== selectedValue) {
      onSelect(value);
    }
    setOpen(false);
  };

  const selectedLabel = items.find(
    (item) => item.value === selectedValue
  )?.listItem;

  // as this item serves the role of a heading, props that lead to an empty list or an
  // invalid selected item are an error. no valid component state exists when these
  // are not provided
  if (!selectedLabel) {
    throw new Error('No valid item selected in the Selector component.');
  }

  const panel = (
    <div className={classes.panel}>
      {items.map((item) => (
        <Typography
          variant="h5"
          color="textPrimary"
          className={classes.item}
          key={item.value}
          onClick={handleSelect(item.value)}>
          {item.listItem}
        </Typography>
      ))}
    </div>
  );

  return (
    <div ref={node} className={classes.wrapper} onClick={() => setOpen(true)}>
      <Typography variant={labelVariant} color="textPrimary">
        {selectedLabel}
      </Typography>
      <div className={classes.icon}>
        <BigChevronDownIcon />
      </div>
      {open && panel}
    </div>
  );
};

export default Selector;
